import type { AspectRatio } from '@lumen5/luminary';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Model } from 'js/src/agent/types';
import type { NarrativeThreadEnum } from 'js/src/client';

export interface SketchControlsState {
    blueprintId: number | null;
    templateId: string | null;
    narrativeThread: NarrativeThreadEnum | null;
    durationSeconds: number | null;
    aspectRatio: AspectRatio | null;
    voiceId: string | null;
    model: Model | null;
    brandKitId: number | null;
    cookiePersistenceEnabled: boolean;
}

const initialState: SketchControlsState = {
    blueprintId: null,
    templateId: null,
    narrativeThread: null,
    durationSeconds: null,
    aspectRatio: null,
    voiceId: null,
    model: null,
    brandKitId: null,
    cookiePersistenceEnabled: true,
};

const sketchControlsSlice = createSlice({
    name: 'sketchControls',
    initialState,
    reducers: {
        setBlueprint: (state, action: PayloadAction<number>) => {
            state.blueprintId = action.payload;
        },
        clearBlueprint: (state) => {
            state.blueprintId = null;
        },
        setTemplate: (state, action: PayloadAction<string>) => {
            state.templateId = action.payload;
        },
        clearTemplate: (state) => {
            state.templateId = null;
        },
        setNarrativeThread: (state, action: PayloadAction<NarrativeThreadEnum>) => {
            state.narrativeThread = action.payload;
        },
        clearNarrativeThread: (state) => {
            state.narrativeThread = null;
        },
        setDuration: (state, action: PayloadAction<number>) => {
            state.durationSeconds = action.payload;
        },
        clearDuration: (state) => {
            state.durationSeconds = null;
        },
        setAspectRatio: (state, action: PayloadAction<AspectRatio>) => {
            state.aspectRatio = action.payload;
        },
        clearAspectRatio: (state) => {
            state.aspectRatio = null;
        },
        setVoice: (state, action: PayloadAction<string>) => {
            state.voiceId = action.payload;
        },
        clearVoice: (state) => {
            state.voiceId = null;
        },
        setModel: (state, action: PayloadAction<Model>) => {
            state.model = action.payload;
        },
        clearModel: (state) => {
            state.model = null;
        },
        setBrandKit: (state, action: PayloadAction<number>) => {
            state.brandKitId = action.payload;
        },
        clearBrandKit: (state) => {
            state.brandKitId = null;
        },
        loadFromCookie: (state, action: PayloadAction<Partial<SketchControlsState>>) => {
            state.blueprintId = action.payload.blueprintId ?? null;
            state.templateId = action.payload.templateId ?? null;
            state.narrativeThread = action.payload.narrativeThread ?? null;
            state.durationSeconds = action.payload.durationSeconds ?? null;
            state.aspectRatio = action.payload.aspectRatio ?? null;
            state.voiceId = action.payload.voiceId ?? null;
            state.model = action.payload.model ?? null;
            state.brandKitId = action.payload.brandKitId ?? null;
        },
        clearAllControls: (state) => {
            state.blueprintId = null;
            state.templateId = null;
            state.narrativeThread = null;
            state.durationSeconds = null;
            state.aspectRatio = null;
            state.voiceId = null;
            // Note: brandKitId and model are intentionally NOT cleared here.
            // brandKitId must persist so CreateVideoTool can fetch and apply it; model must
            // persist so the user's pick keeps governing the conversation after the first
            // message instead of falling back to the default.
        },
        disableCookiePersistence: (state) => {
            state.cookiePersistenceEnabled = false;
        },
        enableCookiePersistence: (state) => {
            state.cookiePersistenceEnabled = true;
        },
    },
});

export const {
    setBlueprint,
    clearBlueprint,
    setTemplate,
    clearTemplate,
    setNarrativeThread,
    clearNarrativeThread,
    setDuration,
    clearDuration,
    setAspectRatio,
    clearAspectRatio,
    setVoice,
    clearVoice,
    setModel,
    clearModel,
    setBrandKit,
    clearBrandKit,
    loadFromCookie,
    clearAllControls,
    disableCookiePersistence,
    enableCookiePersistence,
} = sketchControlsSlice.actions;

export default sketchControlsSlice.reducer;
